import React from 'react'
import { CommentAnchorType } from '@marketing-milk/interfaces'
import { SurfacePalette, type } from '../../tokens'
import { CommentIndicator } from '../../components/CommentIndicator'

interface Props {
  palette: SurfacePalette
  /** Two-digit section number, e.g. "01". */
  number: string
  title: string
  /** Optional word inside the title rendered in italic accent. */
  accentWord?: string
  commentAnchor?: { type: CommentAnchorType; id: string; label?: string }
  children: React.ReactNode
}

const renderTitle = (title: string, accentWord: string | undefined, accent: string) => {
  if (!accentWord) return title
  const idx = title.indexOf(accentWord)
  if (idx < 0) return title
  return (
    <>
      {title.slice(0, idx)}
      <em style={{ color: accent, fontStyle: 'italic' }}>{accentWord}</em>
      {title.slice(idx + accentWord.length)}
    </>
  )
}

/**
 * One Present-mode "slide": a large serif section header (the editorial
 * treatment edit mode dropped in v0.12.6) over read-only content. The root
 * carries the `data-comment-anchor-*` attributes so the shared comment-trigger
 * delegation in `PacketPresentBody` keeps working unchanged.
 */
export const PresentPage = ({
  palette,
  number,
  title,
  accentWord,
  commentAnchor,
  children,
}: Props) => (
  <div
    data-comment-anchor-type={commentAnchor?.type}
    data-comment-anchor-id={commentAnchor?.id}
    data-comment-label={commentAnchor?.label}
    style={{ display: 'flex', flexDirection: 'column', color: palette.text }}
  >
    <header
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 24,
        marginBottom: 36,
        paddingBottom: 20,
        borderBottom: `1px solid ${palette.hairline}`,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: type.family.mono,
            fontSize: type.size.eyebrow,
            fontWeight: type.weight.medium,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: palette.accent,
            marginBottom: 14,
          }}
        >
          {number}
        </div>
        <h1
          style={{
            margin: 0,
            fontFamily: type.family.serif,
            fontSize: type.size.display,
            fontWeight: type.weight.regular,
            lineHeight: 1.04,
          }}
        >
          {renderTitle(title, accentWord, palette.accent)}
        </h1>
      </div>
      {commentAnchor && (
        <div style={{ flexShrink: 0 }}>
          <CommentIndicator palette={palette} label={title} />
        </div>
      )}
    </header>
    {children}
  </div>
)
